import { createContext, useContext, useMemo } from "react";
import { FormRuleValidationResult } from "src/generated/hooks/form-service/requests";
import { FormEngineRuleFormData } from "./schemas";

type FormFieldName = keyof FormEngineRuleFormData;

export type RuleValidationState = {
  fieldErrors: Partial<Record<FormFieldName, string>>;
  schoolResults: FormRuleValidationResult[];
  generalErrors: string[];
};

export const emptyRuleValidationState: RuleValidationState = {
  fieldErrors: {},
  schoolResults: [],
  generalErrors: [],
};

export const toRuleValidationState = (
  results: FormRuleValidationResult[]
): RuleValidationState => {
  const fieldErrors: Partial<Record<FormFieldName, string>> = {};
  const schoolResults: FormRuleValidationResult[] = [];
  const generalErrors: string[] = [];

  for (const result of results) {
    if (result.level === "School") {
      schoolResults.push(result);
      continue;
    }

    const field =
      result.fieldName === "is_inside"
        ? "inside_outside"
        : (result.fieldName as FormFieldName | undefined);

    if (field) {
      fieldErrors[field] ??= result.error;
    } else {
      generalErrors.push(result.error);
    }
  }

  return { fieldErrors, schoolResults, generalErrors };
};

const schoolErrorsById = (
  schoolResults: FormRuleValidationResult[]
): Map<string, string> =>
  new Map(
    schoolResults.flatMap((result) =>
      result.level === "School"
        ? [[result.schoolId, result.error] as const]
        : []
    )
  );

const ValidationContext = createContext<RuleValidationState>(
  emptyRuleValidationState
);

export const ValidationContextProvider = ValidationContext.Provider;

export const useValidationContext = (): RuleValidationState =>
  useContext(ValidationContext);

export const useFieldError = (field: FormFieldName): string | undefined =>
  useValidationContext().fieldErrors[field];

export const useSchoolErrorById = (): Map<string, string> => {
  const { schoolResults } = useValidationContext();
  return useMemo(() => schoolErrorsById(schoolResults), [schoolResults]);
};

export const SCHOOL_CONFLICT_SUMMARY =
  "Some selected schools are already used by another rule of this type.";
